/**
 * useConnectors -- admin connectors grid (P40.2).
 *
 * Calls GET /v1/admin/twin/connectors -- admin-only. Callers gate
 * the surrounding UI with useMe() so non-admins don't fire the call.
 */
import { useEffect, useRef } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { UseMutationResult, UseQueryResult } from "@tanstack/react-query";

import { api } from "shared/api/client";
import type {
  ConnectorListResponse,
  ConnectorRunQueuedOut,
  ConnectorRunRow,
  LiveConnectorRun,
} from "shared/api/types";

export function useConnectors(
  options: { enabled?: boolean } = {},
): UseQueryResult<ConnectorListResponse> {
  return useQuery<ConnectorListResponse>({
    queryKey: ["admin-connectors"] as const,
    queryFn: () => api.get<ConnectorListResponse>("/v1/admin/twin/connectors"),
    enabled: options.enabled ?? true,
  });
}

export function useConnectorRuns(
  connectorId: string | null,
  options: { enabled?: boolean; limit?: number; rowVersion?: string } = {},
): UseQueryResult<ConnectorRunRow[]> {
  const limit = options.limit ?? 10;
  // Round 9 B3. The panel contradicted itself: the header read "just
  // now (failed) -- Errors 1" while the table inside it read "No runs
  // yet for this connector."
  //
  // The trigger mutation DID invalidate this query. It could not help:
  // a run is dispatched as a BackgroundTask and the endpoint returns 202
  // immediately, so the invalidation refetches before the run exists,
  // and nothing refetches again when it does. Invalidating harder would
  // just be a race run faster.
  //
  // So the history is keyed on the row's own last-run marker. When the
  // list poll notices the run, the key changes and this refetches by
  // itself -- no second invalidation, no polling while the row sits
  // open, and the two halves of the panel cannot disagree because one
  // is derived from the other.
  return useQuery<ConnectorRunRow[]>({
    queryKey: [
      "admin-connector-runs",
      connectorId,
      limit,
      options.rowVersion ?? "",
    ] as const,
    queryFn: () =>
      api.get<ConnectorRunRow[]>(
        `/v1/admin/twin/connectors/${connectorId}/runs?limit=${limit}`,
      ),
    enabled: (options.enabled ?? true) && Boolean(connectorId),
  });
}

export function useTriggerConnectorRun(): UseMutationResult<
  ConnectorRunQueuedOut,
  Error,
  string
> {
  const qc = useQueryClient();
  return useMutation<ConnectorRunQueuedOut, Error, string>({
    mutationFn: (connectorId: string) =>
      api.post<ConnectorRunQueuedOut>(
        `/v1/admin/twin/connectors/${connectorId}/run`,
        {},
      ),
    onSuccess: (_data, connectorId) => {
      void qc.invalidateQueries({ queryKey: ["admin-connectors"] });
      void qc.invalidateQueries({
        queryKey: ["admin-connector-runs", connectorId],
      });
      // The queued row is written before the 202 returns: show it now.
      void qc.invalidateQueries({ queryKey: LIVE_RUNS_KEY });
    },
  });
}

export const LIVE_RUNS_KEY = ["admin-connector-live-runs"] as const;

/** Fast while anything is queued or running, slow otherwise. */
export const LIVE_POLL_ACTIVE_MS = 3000;
export const LIVE_POLL_IDLE_MS = 15000;

/**
 * Connector runs queued or in progress (Alembic 0206).
 *
 * A run's own record is invisible until it ends, and the first live
 * Archer run spent seven minutes with nothing on screen. This polls the
 * live rows -- every few seconds while there are any -- and when one
 * goes, refreshes the connector list and that connector's run history,
 * so the finished run appears without a reload.
 */
export function useLiveConnectorRuns(
  options: { enabled?: boolean } = {},
): UseQueryResult<LiveConnectorRun[]> {
  const qc = useQueryClient();
  const query = useQuery<LiveConnectorRun[]>({
    queryKey: LIVE_RUNS_KEY,
    queryFn: () => api.get<LiveConnectorRun[]>("/v1/admin/twin/connectors/live-runs"),
    enabled: options.enabled ?? true,
    refetchInterval: (q) =>
      Array.isArray(q.state.data) && q.state.data.length > 0
        ? LIVE_POLL_ACTIVE_MS
        : LIVE_POLL_IDLE_MS,
  });
  const seen = useRef<Map<string, string>>(new Map());
  useEffect(() => {
    const rows = Array.isArray(query.data) ? query.data : [];
    const now = new Map(rows.map((r) => [r.id, r.connector_id]));
    const ended = [...seen.current.entries()].filter(([id]) => !now.has(id));
    if (ended.length > 0) {
      void qc.invalidateQueries({ queryKey: ["admin-connectors"] });
      for (const [, connectorId] of ended) {
        void qc.invalidateQueries({ queryKey: ["admin-connector-runs", connectorId] });
      }
    }
    seen.current = now;
  }, [query.data, qc]);
  return query;
}
